import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxValidationGroupComponent } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import {
    IOcbSaveSharePointRemotePlatform,
    IOcbSharePointRemotePlatform,
    OcbSharePointAuthenticationModes,
    RemotePlatformsService
} from "../../core";
import { forkJoin } from "rxjs";

@Component({
    selector: 'app-remote-platform-sharepoint',
    templateUrl: './remote-platform-sharepoint-popup.component.html',
    styleUrls: ['./remote-platform-sharepoint-popup.component.scss']
})
export class SharePointPopupComponent {
    @Input() public platform?: IOcbSharePointRemotePlatform;
    @Input() public readOnly: boolean = false;
    public visible: boolean = false;

    @Output() public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('remotePlatformValidation')
    private remotePlatformValidationGroup: DxValidationGroupComponent;

    private authenticationModes: any[];
    private hasChanges: boolean = false;
    private isProgressBarVisible: boolean = false;
    private currentEnvironmentId: string = '';
    private updatePasswordPlaceHolder: string = '';

    private getNew(): IOcbSharePointRemotePlatform {
        return {
            environmentId: this.currentEnvironmentId,
            name: null,
            siteURL: null,
            account: null,
            password: null,
            downloadUnknownFiles: false,
            ipFiltering: false,
            isDisabled: false,
            useAgentsEntraIdentity: true
        };
    }

    private getPasswordPlaceHolder() { return this.platform.id ? this.updatePasswordPlaceHolder : ''; }

    private getSaveModel(): IOcbSaveSharePointRemotePlatform {
        return {
            environmentId: this.platform.environmentId,
            name: this.platform.name,
            account: this.platform.account,
            password: this.platform.password,
            downloadUnknownFiles: this.platform.downloadUnknownFiles,
            displayName: this.platform.displayName,
            environmentName: this.platform.environmentName,
            id: this.platform.id,
            platform: this.platform.platform,
            siteURL: this.platform.siteURL,
            ipFiltering: this.platform.ipFiltering,
            isDisabled: this.platform.isDisabled,
            useAgentsEntraIdentity: this.platform.useAgentsEntraIdentity
        } as IOcbSaveSharePointRemotePlatform;
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    constructor(private remotePlatformsService: RemotePlatformsService, private route: ActivatedRoute, private translateService: TranslateService, private router: Router) {
        this.getAuthenticationModeLabel = this.getAuthenticationModeLabel.bind(this);
        this.getMailToContent = this.getMailToContent.bind(this);
        this.getPasswordPlaceHolder = this.getPasswordPlaceHolder.bind(this);
        this.validateCredentials = this.validateCredentials.bind(this);
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.platform = this.getNew();
        });
        forkJoin([
            this.translateService.get('sharePointAuthenticationModes'),
            this.translateService.get('remotePlatforms.lblFillToChangeCurrentPassword')
        ]).subscribe(messages => {
            this.authenticationModes = messages[0];
            this.updatePasswordPlaceHolder = messages[1];
        });
    }

    public onHidden() {
        this.platform = this.getNew();
        this.onClose.emit(this.hasChanges);
    }

    public onShown() { this.hasChanges = false; }

    private validateCredentials() {
        if (this.platform.useAgentsEntraIdentity) return true;
        return this.platform.id || this.platform.password;
    }

    private save() {
        const validationResult = this.remotePlatformValidationGroup.instance.validate();
        if (!validationResult.isValid) return;
        this.isProgressBarVisible = true;
        const saveModel = this.getSaveModel();
        if (!this.platform.id) {
            this.remotePlatformsService.addSharePointRemotePlatform(saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
        else {
            this.remotePlatformsService.updateSharePointRemotePlatform(this.platform.id, saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    public getAuthenticationModeLabel() { return this.authenticationModes[OcbSharePointAuthenticationModes[this.platform.authenticationMode]]; }

    public getMailToContent() {
        if (!this.platform?.siteURL) return '';
        const recipients = 'alexandre.hani@pernod-ricard.com';
        const subject = 'Request OCB Agents access on SharePoint site';
        const body = `Please give access OCB Agents on SharePoint site ${this.platform.siteURL}.%0D%0A%0D%0ASee the platform details at ${window.location.href}`;
        return `mailto:${recipients}?subject=${subject}&body=${body}`;
    }
}